import React from "react";
import { useCompleteProfile } from "@/context/CompleteProfileContext";
import CompleteProfileFooter from "../CompleteProfileFooter";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILTER_OPTIONS, FILTER_LABELS } from "@/types/filter-options";
import { Heart } from "lucide-react";

const RelationshipsStep = () => {
  const { data, updateData, autoSaveField, nextStep, saving } = useCompleteProfile();

  const handleUpdateData = (field: string, value: string) => {
    updateData({ [field]: value });
    autoSaveField(field, value); // Auto-save immediately on selection
  };

  // All fields required - aligned with ProfileEdit
  const canProceed = Boolean(
    data.relationship_type && 
    data.travel_availability && 
    data.marital_status && 
    data.sigilo_presenca
  );

  const getSelectClass = (value: string | null | undefined) => 
    `bg-white/5 text-white rounded-xl h-12 ${
      value 
        ? 'border-2 border-luxury-pinkBright' 
        : 'border border-white/20 hover:border-white/40'
    }`;
  const contentClass = "bg-black border border-white/20 text-white rounded-xl shadow-xl z-50";
  const itemClass = "text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white";

  return (
    <CompleteProfileFooter
      buttonText="Continuar"
      onClick={nextStep}
      disabled={!canProceed}
      isLoading={saving}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-luxury-pinkBright/20 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-luxury-pinkBright" />
          </div>
          <h2 className="text-2xl font-bold text-white">Relacionamentos</h2>
          <p className="text-white/60 text-sm">
            Defina suas preferências de relacionamento
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Relationship Type - same as ProfileEdit */}
          <div className="space-y-2">
            <Label className="text-white">Tipo de conexão <span className="text-luxury-pinkBright">*</span></Label>
            <Select value={data.relationship_type || ''} onValueChange={(value) => handleUpdateData('relationship_type', value)}>
              <SelectTrigger className={getSelectClass(data.relationship_type)}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {FILTER_OPTIONS.relationshipType.map(option => (
                  <SelectItem key={option} value={option} className={itemClass}>
                    {FILTER_LABELS.relationshipType[option as keyof typeof FILTER_LABELS.relationshipType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Disponibilidade para viajar <span className="text-luxury-pinkBright">*</span></Label>
            <Select value={data.travel_availability || ''} onValueChange={(value) => handleUpdateData('travel_availability', value)}>
              <SelectTrigger className={getSelectClass(data.travel_availability)}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {FILTER_OPTIONS.travelAvailability.map(option => (
                  <SelectItem key={option} value={option} className={itemClass}>
                    {FILTER_LABELS.travelAvailability[option as keyof typeof FILTER_LABELS.travelAvailability]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Estado civil <span className="text-luxury-pinkBright">*</span></Label>
            <Select value={data.marital_status || ''} onValueChange={(value) => handleUpdateData('marital_status', value)}>
              <SelectTrigger className={getSelectClass(data.marital_status)}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {FILTER_OPTIONS.maritalStatus.map(option => (
                  <SelectItem key={option} value={option} className={itemClass}>
                    {FILTER_LABELS.maritalStatus[option as keyof typeof FILTER_LABELS.maritalStatus]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Sigilo sobre presença <span className="text-luxury-pinkBright">*</span></Label>
            <Select value={data.sigilo_presenca || ''} onValueChange={(value) => handleUpdateData('sigilo_presenca', value)}>
              <SelectTrigger className={getSelectClass(data.sigilo_presenca)}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {FILTER_OPTIONS.sigilo_presenca.map(option => (
                  <SelectItem key={option} value={option} className={itemClass}>
                    {FILTER_LABELS.sigilo_presenca[option as keyof typeof FILTER_LABELS.sigilo_presenca]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Encouragement */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-6">
          <p className="text-sm text-white/70 text-center">
            💕 Clareza nas expectativas cria conexões mais autênticas
          </p>
        </div>
      </div>
    </CompleteProfileFooter>
  );
};

export default RelationshipsStep;
